'use strict'
import * as React from 'react'
import { useJudgmentRuleSettingDispatch, useJudgmentRuleSettingSelector } from '../extra/store'
import { useToast } from '../../../../_components/Toast'
import { useModal, useModalExtra } from '../../../../_components/Modal'
import { getChrofyColumnsByDataClassificationCode, buildSidebarGenericDataKindClassifications } from '../extra/services'
import { RankFunctionModalContent } from './RankFunctionModal'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'

/**
 * サイドバーのデータ分類選択プルダウン（矢印が開閉で回転する共通プルダウンに統一）。
 *
 * @param {Object} props
 * @param {string} props.value 選択中のデータ分類コード
 * @param {(e: { target: { value: string } }) => void} props.onChange
 * @param {boolean} props.disabled
 * @param {{ DataClassificationCode: string; DataClassificationName: string }[]} props.classifications
 */
const SidebarCategorySelect = ({ value, onChange, disabled, classifications }) => (
  <ChrofyCustomSelect
    className="w-full min-w-0 mb-2.5"
    value={value}
    onChange={onChange}
    disabled={disabled}
    hasNoSelect={false}
    placeholder="データ分類を選択"
    options={[
      { label: 'データ分類を選択', value: '' },
      ...(classifications ?? []).map((cat) => ({
        label: cat.DataClassificationName,
        value: cat.DataClassificationCode
      }))
    ]}
  />
)

/**
 * 条件式入力サイドバーコンポーネント
 *
 * @param {Object} props
 * @param {boolean} props.isReadOnly 編集不可フラグ
 */
const ConditionSidebar = ({ isReadOnly = false }) => {
  const dispatch = useJudgmentRuleSettingDispatch()
  const showToast = useToast()
  const {
    periodKind,
    dataType,
    dataClassifications,
    genericDataKinds,
    chrofyColumns,
    currentYearCategory,
    previousYearCategory,
    currentMonthCategory,
    previousMonthCategory,
    currentYearEndMonthCategory,
    previousYearEndMonthCategory,
    selectedConditionIndex
  } = useJudgmentRuleSettingSelector((state) => ({
    periodKind: state.periodKind,
    dataType: state.dataType,
    dataClassifications: state.dataClassifications,
    genericDataKinds: state.genericDataKinds,
    chrofyColumns: state.chrofyColumns,
    currentYearCategory: state.currentYearCategory,
    previousYearCategory: state.previousYearCategory,
    currentMonthCategory: state.currentMonthCategory,
    previousMonthCategory: state.previousMonthCategory,
    currentYearEndMonthCategory: state.currentYearEndMonthCategory,
    previousYearEndMonthCategory: state.previousYearEndMonthCategory,
    selectedConditionIndex: state.selectedConditionIndex
  }))

  // 対象期間が月次のときはサイドバーも月次として扱う（ラジオ非表示・内部で月次ON）
  // 対象期間が四半期のときはサイドバーも四半期として扱う
  // 対象期間が年次のときは dataType が yearly / yearEndMonthly（従来の monthly は yearEndMonthly として表示）
  const effectiveDataType =
    periodKind === 'monthly'
      ? 'monthly'
      : periodKind === 'quarter'
        ? 'quarterly'
        : dataType === 'monthly'
          ? 'yearEndMonthly'
          : dataType

  // サイドバー データ分類: genericDataKinds を TargetPeriod で絞り込んだ一覧
  // 年次データのとき "year"、月次データ・年度末月次データのとき "monthly" を含むタスクを表示
  const sidebarDataClassifications = React.useMemo(
    () => buildSidebarGenericDataKindClassifications(genericDataKinds, effectiveDataType),
    [genericDataKinds, effectiveDataType]
  )

  const handleDataTypeChange = (type) => {
    dispatch('SET_DATA_TYPE', type)
  }

  const handleCategoryChange = (type, category) => {
    switch (type) {
      case 'currentYear':
        dispatch('SET_CURRENT_YEAR_CATEGORY', category)
        break
      case 'previousYear':
        dispatch('SET_PREVIOUS_YEAR_CATEGORY', category)
        break
      case 'currentMonth':
        dispatch('SET_CURRENT_MONTH_CATEGORY', category)
        break
      case 'previousMonth':
        dispatch('SET_PREVIOUS_MONTH_CATEGORY', category)
        break
      case 'currentYearEndMonth':
        dispatch('SET_CURRENT_YEAR_END_MONTH_CATEGORY', category)
        break
      case 'previousYearEndMonth':
        dispatch('SET_PREVIOUS_YEAR_END_MONTH_CATEGORY', category)
        break
    }
  }

  const handleInsertField = (field) => {
    if (selectedConditionIndex < 0) {
      showToast('⚠️ 先に条件式をクリックして選択してください')
      return
    }
    dispatch('INSERT_TO_CONDITION', { text: field })
  }

  const handleInsertOperator = (operator) => {
    if (selectedConditionIndex < 0) {
      showToast('⚠️ 先に条件式をクリックして選択してください')
      return
    }
    dispatch('INSERT_TO_CONDITION', { text: operator })
  }

  const handleClearCondition = () => {
    if (selectedConditionIndex < 0) {
      showToast('⚠️ 先に条件式をクリックして選択してください')
      return
    }
    dispatch('CLEAR_CONDITION')
  }

  const handleDeleteLastChar = () => {
    if (selectedConditionIndex < 0) {
      showToast('⚠️ 先に条件式をクリックして選択してください')
      return
    }
    dispatch('DELETE_LAST_CHAR')
  }

  const [valueInput, setValueInput] = React.useState('')
  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()

  const handleInsertValue = () => {
    if (selectedConditionIndex < 0) {
      showToast('⚠️ 先に条件式をクリックして選択してください')
      return
    }
    const val = valueInput.trim()
    // 空の場合は条件式に(空)を追加（実際の値は空文字としてAPI送信時に変換される）
    const insertText = val === '' ? '(空)' : isNaN(val) ? `"${val}"` : val
    dispatch('INSERT_TO_CONDITION', { text: insertText })
    setValueInput('')
  }

  // サイドバー: データ分類に応じたデータ項目をフィルタリング（chrofyColumns）
  const getFilteredColumns = (category) => getChrofyColumnsByDataClassificationCode(chrofyColumns, category)

  const handleOpenRankModal = () => {
    if (selectedConditionIndex < 0) {
      showToast('⚠️ 先に条件式をクリックして選択してください')
      return
    }
    if (periodKind !== 'monthly' && periodKind !== 'quarter' && periodKind !== 'yearly') {
      showToast('⚠️ 先に対象期間（月次・四半期または年次）を選択してください')
      return
    }
    showModal({
      title: '🔧 RANK関数',
      body: (
        <RankFunctionModalContent
          periodKind={periodKind}
          dataClassifications={dataClassifications}
          onInsert={(text) => {
            dispatch('INSERT_TO_CONDITION', { text })
            _closeUpModal()
            showToast('✔ 関数を挿入しました')
          }}
          onClose={_closeUpModal}
        />
      ),
      isFooter: false,
      isCancel: false,
      isWide: false
    })
  }

  return (
    <div>
      <div className="bg-white rounded-lg shadow-sm mb-5 border border-gray-300">
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-gray-200 font-semibold flex items-center gap-2 text-base bg-white rounded-t-lg">
          ✏️ 直接入力
        </div>
        <div className="p-6">
          <div className="mb-[18px] last:mb-0">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">📦 項目</div>
            {/* 対象期間が未選択のときはラジオ・データ分類を非表示。選択時のみ表示 */}
            {periodKind && (
              <>
                {/* 対象期間が年次のときのみデータタイプのラジオを表示。月次のときは非表示で内部的に月次として扱う */}
                {periodKind === 'yearly' && (
                  <div className="mb-[14px]">
                    <div className="text-xs font-semibold text-blue1 mb-2">データタイプ</div>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="dataType"
                          value="yearly"
                          checked={dataType === 'yearly'}
                          onChange={() => handleDataTypeChange('yearly')}
                          className="mr-1.5 cursor-pointer"
                          disabled={isReadOnly}
                        />
                        <span className="text-xs">年次データ</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="dataType"
                          value="yearEndMonthly"
                          checked={dataType === 'yearEndMonthly' || dataType === 'monthly'}
                          onChange={() => handleDataTypeChange('yearEndMonthly')}
                          className="mr-1.5 cursor-pointer"
                          disabled={isReadOnly}
                        />
                        <span className="text-xs">年度末月次データ</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* 年次データセクション（今年度データ・前年度データ）。対象期間が年次かつデータタイプが年次のとき表示 */}
                {effectiveDataType === 'yearly' && (
                  <div id="yearlyDataSection">
                    <div className="mb-[14px]">
                      <div className="text-xs font-semibold text-blue1 mb-2">今年度データ</div>
                      <SidebarCategorySelect
                        value={currentYearCategory}
                        onChange={(e) => handleCategoryChange('currentYear', e.target.value)}
                        disabled={isReadOnly}
                        classifications={sidebarDataClassifications}
                      />
                      {currentYearCategory && (
                        <div>
                          {getFilteredColumns(currentYearCategory).map((col) => (
                            <button
                              key={col.ColumnCode}
                              className="inline-block px-3 py-2 text-xs bg-white border border-gray-200 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-blue-50 hover:border-blue1 hover:text-blue1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleInsertField(`[今年度.${col.ColumnName}]`)}
                              disabled={isReadOnly}
                            >
                              {col.ColumnName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mb-[14px] mt-4">
                      <div className="text-xs font-semibold text-blue1 mb-2">前年度データ</div>
                      <SidebarCategorySelect
                        value={previousYearCategory}
                        onChange={(e) => handleCategoryChange('previousYear', e.target.value)}
                        disabled={isReadOnly}
                        classifications={sidebarDataClassifications}
                      />
                      {previousYearCategory && (
                        <div>
                          {getFilteredColumns(previousYearCategory).map((col) => (
                            <button
                              key={col.ColumnCode}
                              className="inline-block px-3 py-2 text-xs bg-white border border-gray-200 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-blue-50 hover:border-blue1 hover:text-blue1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleInsertField(`[前年度.${col.ColumnName}]`)}
                              disabled={isReadOnly}
                            >
                              {col.ColumnName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 年度末月次データセクション（今年度末月次データ・前年度末月次データ）。対象期間が年次かつデータタイプが年度末月次のとき表示 */}
                {effectiveDataType === 'yearEndMonthly' && (
                  <div id="yearEndMonthlyDataSection">
                    <div className="mb-[14px]">
                      <div className="text-xs font-semibold text-blue1 mb-2">今年度末月次データ</div>
                      <SidebarCategorySelect
                        value={currentYearEndMonthCategory}
                        onChange={(e) => handleCategoryChange('currentYearEndMonth', e.target.value)}
                        disabled={isReadOnly}
                        classifications={sidebarDataClassifications}
                      />
                      {currentYearEndMonthCategory && (
                        <div>
                          {getFilteredColumns(currentYearEndMonthCategory).map((col) => (
                            <button
                              key={col.ColumnCode}
                              className="inline-block px-3 py-2 text-xs bg-white border border-gray-200 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-blue-50 hover:border-blue1 hover:text-blue1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleInsertField(`[今年度末月次.${col.ColumnName}]`)}
                              disabled={isReadOnly}
                            >
                              {col.ColumnName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mb-[14px] mt-4">
                      <div className="text-xs font-semibold text-blue1 mb-2">前年度末月次データ</div>
                      <SidebarCategorySelect
                        value={previousYearEndMonthCategory}
                        onChange={(e) => handleCategoryChange('previousYearEndMonth', e.target.value)}
                        disabled={isReadOnly}
                        classifications={sidebarDataClassifications}
                      />
                      {previousYearEndMonthCategory && (
                        <div>
                          {getFilteredColumns(previousYearEndMonthCategory).map((col) => (
                            <button
                              key={col.ColumnCode}
                              className="inline-block px-3 py-2 text-xs bg-white border border-gray-200 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-blue-50 hover:border-blue1 hover:text-blue1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleInsertField(`[前年度末月次.${col.ColumnName}]`)}
                              disabled={isReadOnly}
                            >
                              {col.ColumnName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 四半期データセクション（今四半期データ・前四半期データ）。対象期間が四半期のときは常に表示 */}
                {effectiveDataType === 'quarterly' && (
                  <div id="quarterlyDataSection">
                    <div className="mb-[14px]">
                      <div className="text-xs font-semibold text-blue1 mb-2">今四半期データ</div>
                      <SidebarCategorySelect
                        value={currentMonthCategory}
                        onChange={(e) => handleCategoryChange('currentMonth', e.target.value)}
                        disabled={isReadOnly}
                        classifications={sidebarDataClassifications}
                      />
                      {currentMonthCategory && (
                        <div>
                          {getFilteredColumns(currentMonthCategory).map((col) => (
                            <button
                              key={col.ColumnCode}
                              className="inline-block px-3 py-2 text-xs bg-white border border-gray-200 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-blue-50 hover:border-blue1 hover:text-blue1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleInsertField(`[今四半期.${col.ColumnName}]`)}
                              disabled={isReadOnly}
                            >
                              {col.ColumnName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mb-[14px] mt-4">
                      <div className="text-xs font-semibold text-blue1 mb-2">前四半期データ</div>
                      <SidebarCategorySelect
                        value={previousMonthCategory}
                        onChange={(e) => handleCategoryChange('previousMonth', e.target.value)}
                        disabled={isReadOnly}
                        classifications={sidebarDataClassifications}
                      />
                      {previousMonthCategory && (
                        <div>
                          {getFilteredColumns(previousMonthCategory).map((col) => (
                            <button
                              key={col.ColumnCode}
                              className="inline-block px-3 py-2 text-xs bg-white border border-gray-200 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-blue-50 hover:border-blue1 hover:text-blue1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleInsertField(`[前四半期.${col.ColumnName}]`)}
                              disabled={isReadOnly}
                            >
                              {col.ColumnName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 月次データセクション（今月データ・前月データ）。対象期間が月次のときは常に表示 */}
                {effectiveDataType === 'monthly' && (
                  <div id="monthlyDataSection">
                    <div className="mb-[14px]">
                      <div className="text-xs font-semibold text-blue1 mb-2">今月データ</div>
                      <SidebarCategorySelect
                        value={currentMonthCategory}
                        onChange={(e) => handleCategoryChange('currentMonth', e.target.value)}
                        disabled={isReadOnly}
                        classifications={sidebarDataClassifications}
                      />
                      {currentMonthCategory && (
                        <div>
                          {getFilteredColumns(currentMonthCategory).map((col) => (
                            <button
                              key={col.ColumnCode}
                              className="inline-block px-3 py-2 text-xs bg-white border border-gray-200 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-blue-50 hover:border-blue1 hover:text-blue1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleInsertField(`[今月.${col.ColumnName}]`)}
                              disabled={isReadOnly}
                            >
                              {col.ColumnName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mb-[14px] mt-4">
                      <div className="text-xs font-semibold text-blue1 mb-2">前月データ</div>
                      <SidebarCategorySelect
                        value={previousMonthCategory}
                        onChange={(e) => handleCategoryChange('previousMonth', e.target.value)}
                        disabled={isReadOnly}
                        classifications={sidebarDataClassifications}
                      />
                      {previousMonthCategory && (
                        <div>
                          {getFilteredColumns(previousMonthCategory).map((col) => (
                            <button
                              key={col.ColumnCode}
                              className="inline-block px-3 py-2 text-xs bg-white border border-gray-200 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-blue-50 hover:border-blue1 hover:text-blue1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleInsertField(`[前月.${col.ColumnName}]`)}
                              disabled={isReadOnly}
                            >
                              {col.ColumnName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="h-px bg-gray-200 my-[18px]"></div>

          <div className="mb-[18px] last:mb-0">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">🔧 関数</div>
            <button
              type="button"
              className="inline-block px-3 py-2 text-xs bg-yellow-100 border border-yellow-400 rounded cursor-pointer mr-1 mb-1.5 transition-all hover:bg-yellow-200 hover:border-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isReadOnly || (periodKind !== 'monthly' && periodKind !== 'quarter' && periodKind !== 'yearly')}
              onClick={handleOpenRankModal}
            >
              RANK() - 等級→順位
            </button>
            <p className="text-xs text-gray-500 mt-1.5">クリックでガイド付き入力</p>
          </div>
          <div className="h-px bg-gray-200 my-[18px]"></div>

          <div className="mb-[18px] last:mb-0">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">🔍 比較演算子</div>
            <div className="flex flex-wrap gap-1">
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' > ')}
                disabled={isReadOnly}
              >
                ＞
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' < ')}
                disabled={isReadOnly}
              >
                ＜
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' >= ')}
                disabled={isReadOnly}
              >
                ≧
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' <= ')}
                disabled={isReadOnly}
              >
                ≦
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' == ')}
                disabled={isReadOnly}
              >
                ＝
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' != ')}
                disabled={isReadOnly}
              >
                ≠
              </button>
            </div>
          </div>

          <div className="mb-[18px] last:mb-0 mt-3.5">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">➕ 計算演算子</div>
            <div className="flex flex-wrap gap-1">
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' + ')}
                disabled={isReadOnly}
              >
                ＋
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' - ')}
                disabled={isReadOnly}
              >
                －
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' * ')}
                disabled={isReadOnly}
              >
                ×
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' / ')}
                disabled={isReadOnly}
              >
                ÷
              </button>
            </div>
          </div>

          <div className="mb-[18px] last:mb-0 mt-3.5">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">（） 括弧</div>
            <div className="flex flex-wrap gap-1">
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator('(')}
                disabled={isReadOnly}
              >
                (
              </button>
              <button
                className="px-2.5 py-2 bg-gray-100 border border-gray-200 rounded text-[13px] cursor-pointer transition-all text-center min-w-[36px] hover:bg-[#3150A5] hover:text-white hover:border-[#3150A5] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(')')}
                disabled={isReadOnly}
              >
                )
              </button>
            </div>
          </div>

          <div className="mb-[18px] last:mb-0 mt-3.5">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">🔗 論理演算子</div>
            <div className="flex flex-wrap gap-2">
              <button
                className="px-3.5 py-2.5 bg-yellow-100 border border-yellow-400 rounded text-xs cursor-pointer transition-all text-center min-w-[48px] text-yellow-800 hover:bg-yellow-500 hover:text-white hover:border-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' AND ')}
                disabled={isReadOnly}
              >
                かつ
              </button>
              <button
                className="px-3.5 py-2.5 bg-yellow-100 border border-yellow-400 rounded text-xs cursor-pointer transition-all text-center min-w-[48px] text-yellow-800 hover:bg-yellow-500 hover:text-white hover:border-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleInsertOperator(' OR ')}
                disabled={isReadOnly}
              >
                または
              </button>
            </div>
          </div>

          <div className="h-px bg-gray-200 my-[18px]"></div>

          <div className="mb-[18px] last:mb-0">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">📤 値の入力</div>
            <div className="flex gap-2.5">
              <input
                type="text"
                className="flex-1 p-3 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#3150A5] focus:shadow-[0_0_0_3px_rgba(49,80,165,0.1)]"
                placeholder="数値または文字列"
                value={valueInput}
                onChange={(e) => setValueInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleInsertValue()
                  }
                }}
                disabled={isReadOnly}
              />
              <button
                className="px-[18px] py-3 bg-[#3150A5] text-white border-none rounded text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-[#264089] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleInsertValue}
                disabled={isReadOnly}
              >
                反映
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">文字列は自動で " " で囲まれます</p>
          </div>

          <div className="h-px bg-gray-200 my-[18px]"></div>

          <div className="mb-[18px] last:mb-0">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">🗑️ 操作</div>
            <div className="flex gap-2">
              <button
                className="flex-1 p-3 bg-red-100 border border-red-300 rounded text-red-800 text-sm font-medium cursor-pointer transition-all hover:bg-red-200 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDeleteLastChar}
                disabled={isReadOnly}
              >
                ←削除
              </button>
              <button
                className="flex-1 p-3 bg-red-100 border border-red-300 rounded text-red-800 text-sm font-medium cursor-pointer transition-all hover:bg-red-200 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleClearCondition}
                disabled={isReadOnly}
              >
                ×式をクリア
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConditionSidebar
